/**
 * Registry vendors, as the platform console sees them.
 *
 * READ-ONLY, and deliberately so. The tenant panel lets somebody enter
 * credentials, run a dry run and verify the result; none of that belongs here.
 * Verification is a judgement made by comparing mapped rows against the
 * vendor's own console, and a platform admin looking at fourteen tenants is the
 * person least placed to make it - but most able to click it by accident.
 *
 * What the console DOES need is the answer to "what has this tenant switched
 * on, and how much of it is actually trustworthy" - which is why confidence and
 * the verified flag are the whole content of this panel.
 */

import { useEffect, useState } from 'react';
import { listVendors, errText, type VendorSummary } from './api';

const CAP_LABEL: Record<string, string> = {
  collect: 'collects backups',
  tickets: 'creates tickets',
  history: 'receives history',
  knowledge: 'provides articles',
};

export function VendorsPanel({ orgId }: { orgId: string }) {
  const [vendors, setVendors] = useState<VendorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    listVendors({ orgId })
      .then((r) => { if (live) setVendors(r.vendors); })
      .catch((e) => { if (live) setError(errText(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [orgId]);

  const configured = vendors.filter((v) => v.configured);
  // Everything else is catalogue, not configuration. Collapsed by default so
  // the panel says what this tenant HAS rather than what it could have.
  const rest = vendors.filter((v) => !v.configured);
  const shown = showAll ? [...configured, ...rest] : configured;

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h3 className="panel-title" style={{ marginBottom: 2 }}>Additional platforms</h3>
          <div className="dim" style={{ fontSize: 11 }}>
            Written from vendor documentation. Nothing here writes to a tenant's board until it
            has been dry-run and verified on the tenant side.
          </div>
        </div>
        <button className="btn-link" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'only configured' : `show all ${vendors.length}`}
        </button>
      </div>

      {error && <div className="banner banner-error">{error}</div>}
      {loading ? <div className="empty">Loading…</div> : shown.length === 0 ? (
        <div className="empty">
          This tenant has not configured any additional platforms.{' '}
          <button className="btn-link" onClick={() => setShowAll(true)}>See what is available</button>
        </div>
      ) : (
        <table className="mini-table">
          <thead>
            <tr><th>Platform</th><th>Does</th><th>Confidence</th><th>State</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((v) => (
              <tr key={v.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{v.label}</div>
                  <div className="mono dim" style={{ fontSize: 11 }}>{v.id}</div>
                </td>
                <td className="dim" style={{ fontSize: 11 }}>
                  {v.capabilities.map((c) => CAP_LABEL[c] ?? c).join(' · ')}
                </td>
                <td>
                  {/* Loudest thing in the row on purpose: an unproven mapper and
                      a proven one must never look alike at a glance. */}
                  <span className={
                    v.confidence === 'verified' ? 'tag tag-ok'
                      : v.confidence === 'documented' ? 'tag tag-warn' : 'tag tag-bad'
                  }>{v.confidence}</span>
                </td>
                <td>
                  {!v.configured ? <span className="tag">not configured</span>
                    : v.verified ? <span className="tag tag-ok">verified</span>
                      : <span className="tag tag-warn">not verified</span>}
                  {v.configured && !v.enabled && <span className="tag tag-bad">disabled</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <a className="btn-link" href={v.docs} target="_blank" rel="noreferrer">API docs</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="panel-note">
        Confidence describes the ADAPTER, not the tenant: <span className="mono">documented</span>{' '}
        means the endpoints came from the vendor's own reference,{' '}
        <span className="mono">inferred</span> means they were assembled from SDKs and community
        docs and the mapping will probably need work. Verified is per tenant and is earned by
        running a dry run and comparing the rows against the vendor's console.
      </p>
    </section>
  );
}
